import React, { useState, memo, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import {
  makeSelectAppSettings,
  makeSelectLocale,
  makeSelectThemeColors,
  makeSelectUserDetails,
  makeSelectIsUserLoggedIn,
  makeSelectUserOrGuestUserId
} from "ducks/selectors";
import { requestOtt, setData, disableResendActivation } from "ducks/actions";
import SideMenu from "./SideMenu";
import Header from "./Header";
import Footer from "./Footer";
import get from "lodash/get";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import { Box } from "ui-components";
import { isWebview, getRedirectURL, removeItem } from "utils/localStorage";
import {
  PHONE_ONLY_CHANNEL,
  paths,
  ERROR_CODES,
  PAYMENT_ONLY_MODE
} from "utils/constants";
import {
  getChannelFromUrl,
  gotoUrl,
  urlParam,
  safeEval,
  parseConditionVariables
} from "utils/helpers";

const layoutHOC = WrappedComponent => props => {
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [stickyFooterHeight, setStickyFooterHight] = useState(0);
  const params = new URLSearchParams(window.location.search);
    const isFakeDoor = params.get("fakedoor") === "true";
  const data = get(props, "appSettings.layout.header.profile.data");
  const phoneOnlyWebview =
    getChannelFromUrl() === PHONE_ONLY_CHANNEL && isWebview();
  const paymentOnlyMode = getChannelFromUrl() === PAYMENT_ONLY_MODE;
  const showHeader =
    get(props, "appSettings.layout.header.enable", true) &&
    !phoneOnlyWebview &&
    !paymentOnlyMode && !isFakeDoor;
  const showFooter =
    get(props, "appSettings.layout.footer.enable") && !isWebview();
  const pageStyles = get(props, "appSettings.layout.pagesStyles", []);
  const allowedPageStyle = find(
    pageStyles,
    pageStyle =>
      safeEval(
        get,
        {},
        parseConditionVariables(
          get(props, "appSettings.conditions"),
          get(pageStyle, "condition")
        ),
        false,
        props
      ),
    {}
  );

  let baseStyles = { mx: "auto" };
  let layoutStyles = {
    ...baseStyles,
    maxWidth: 700,
    bg: "transparent",
    p: 0,
    paddingTop: showHeader
      ? urlParam("noPaddingTop") === "true"
        ? 60
        : 100
      : 20,
    paddingBottom: 100,
    ...get(allowedPageStyle, "styles", {})
  };

  const defaultButtons = [
    {
      label: "manage_order",
      url: "/manage/order?reset=true",
      variant: "primary",
      icon: "ViewList"
    }
  ];
  const buttons = get(
    props,
    "appSettings.layout.header.buttons",
    defaultButtons
  );

  useEffect(() => {
    stickyFooterHeight !== window.stickyFooterHeight &&
      setStickyFooterHight(window.stickyFooterHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.stickyFooterHeight]);

  useEffect(() => {
    if (
      isFunction(window?.EvamSdkObject?.changeActorId) &&
      props?.evamActorId &&
      window.EvamSdkObject?.sdkSettings?.authenticated
    ) {
      window.EvamSdkObject.changeActorId(props.evamActorId);
    }
  }, [props.evamActorId, window?.EvamSdkObject?.sdkSettings?.authenticated]);

  return (
    <>
      {showHeader && (
        <SideMenu
          {...props}
          open={sideMenuOpen}
          setOpen={setSideMenuOpen}
          buttons={buttons}
        />
      )}
      <div
        id="page-wrap"
        style={{ marginBottom: stickyFooterHeight, overflowX: "auto" }}
      >
        {showHeader && (
          <Header
            {...props}
            open={sideMenuOpen}
            setOpen={setSideMenuOpen}
            buttons={buttons}
            data={data}
          />
        )}
        <Box as="main" sx={layoutStyles} id="layout">
          <WrappedComponent {...props} />
        </Box>
        {showFooter && <Footer {...props} />}
      </div>
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  appSettings: makeSelectAppSettings(),
  locale: makeSelectLocale(),
  themeColors: makeSelectThemeColors(),
  userDetails: makeSelectUserDetails(),
  isLoggedIn: makeSelectIsUserLoggedIn(),
  evamActorId: makeSelectUserOrGuestUserId()
});

const mapDispatchToProps = dispatch => {
  return {
    requestOTTForActions: props => {
      const params = props?.params || {};
      const timerKey = get(props, "timerKey");

      dispatch(
        requestOtt(params, {}, (res, error) => {
          const success = isEmpty(error);
          if (success && get(res, "requestId")) {
            removeItem(`${timerKey}.timer.startrecord`); // allows timer to start from new time from new API call
            dispatch(disableResendActivation());
          } else {
            error?.errorId === ERROR_CODES.maximumRetryOtt &&
              setTimeout(() => {
                if (isWebview()) {
                  if (getRedirectURL())
                    return gotoUrl(getRedirectURL(), false, {}, true);
                  return gotoUrl(paths.CLOSEWEBVIEW, false, {}, true);
                } else {
                  return gotoUrl(paths.PROFILE);
                }
              }, 0);
          }
        })
      );
    },
    showModal: type => {
      dispatch(
        setData({
          key: "isModalView",
          data: { isModalView: true, type }
        })
      );
    },
    hideModal: () => {
      dispatch(
        setData({
          key: "isModalView",
          data: { isModalView: false, type: "" }
        })
      );
    }
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default component => compose(withConnect, memo)(layoutHOC(component));
